/**
 * SharePromptsSection — "Tips från Farmly" section for the producer dashboard.
 * Fetches pending share prompts and renders prompt cards.
 * Opens Share Studio when a prompt is acted on.
 */

import { useState, useCallback } from 'react';
import { Sparkles } from 'lucide-react';
import { useSharePrompts, useUpdatePromptStatus } from './useSharePrompts';
import { SharePromptCard } from './SharePromptCard';
import { ShareStudioSheet } from '../ShareStudioSheet';
import { features } from '@/config/features';
import { track } from '@/lib/analytics';
import type { EnrichedSharePrompt } from './types';
import type { ActivityShareTarget } from '../ShareTarget';
import { supabase } from '@/integrations/supabase/client';


interface SharePromptsSectionProps {
  producerId: string | undefined;
  producerName: string;
  producerSlug: string;
}

export function SharePromptsSection({ producerId, producerName, producerSlug }: SharePromptsSectionProps) {
  const { data: prompts, isLoading } = useSharePrompts(producerId);
  const { mutate: updateStatus } = useUpdatePromptStatus();
  const [activePrompt, setActivePrompt] = useState<EnrichedSharePrompt | null>(null);
  const [shareTarget, setShareTarget] = useState<ActivityShareTarget | null>(null);
  const [studioOpen, setStudioOpen] = useState(false);

  const handleShare = useCallback(async (prompt: EnrichedSharePrompt) => {
    track('share_prompt_clicked', {
      prompt_type: prompt.prompt_type,
      entity_type: prompt.entity_type,
      entity_id: prompt.entity_id,
      producer_id: prompt.producer_id,
    });

    // Fetch event data for ShareTarget
    if (prompt.entity_type === 'event') {
      const { data: event } = await supabase
        .from('producer_events')
        .select('id, title, start_date, end_date, timing_mode, is_free, price_ore, booking_mode, description, custom_address, status')
        .eq('id', prompt.entity_id)
        .single();

      if (event) {
        setShareTarget({
          type: 'activity',
          id: event.id,
          title: event.title,
          heroImageUrl: null, // Will be resolved by useShareAssets
          producerName,
          producerSlug,
          startDate: event.start_date,
          endDate: event.end_date,
          timingMode: event.timing_mode,
          isFree: event.is_free,
          priceOre: event.price_ore,
          bookingMode: event.booking_mode,
          venueText: event.custom_address,
          descriptionHtml: event.description,
        });
        setActivePrompt(prompt);
        setStudioOpen(true);
      }
    }
  }, [producerName, producerSlug]);

  const handleDismiss = useCallback((promptId: string) => {
    track('share_prompt_dismissed', { prompt_id: promptId });
    updateStatus({ promptId, status: 'dismissed' });
  }, [updateStatus]);

  const handleStudioClose = useCallback((open: boolean) => {
    setStudioOpen(open);
    if (!open && activePrompt) {
      updateStatus({ promptId: activePrompt.id, status: 'completed' });
      setActivePrompt(null);
      setShareTarget(null);
    }
  }, [activePrompt, updateStatus]);

  if (!features.enableShareStudio) return null;
  if (isLoading || !prompts || prompts.length === 0) return null;

  return (
    <section className="mb-8">
      <div className="flex items-center gap-1.5 mb-3">
        <Sparkles className="h-3.5 w-3.5 text-primary" />
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Tips från Farmly
        </h2>
      </div>
      <div className="space-y-2">
        {prompts.map((prompt) => (
          <SharePromptCard
            key={prompt.id}
            prompt={prompt}
            onShare={handleShare}
            onDismiss={handleDismiss}
          />
        ))}
      </div>

      {shareTarget && (
        <ShareStudioSheet
          open={studioOpen}
          onOpenChange={handleStudioClose}
          target={shareTarget}
        />
      )}
    </section>
  );
}
